import type React from "react";
import { useMemo } from "react";

import { EntityIcon } from "metabase/common/components/EntityIcon";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { VirtualizedList } from "metabase/common/components/VirtualizedList";
import { useTranslateContent } from "metabase/content-translation/hooks";
import { getIsTenantUser } from "metabase/current-user";
import { PLUGIN_MODERATION } from "metabase/plugins";
import { useSelector } from "metabase/redux";
import {
  Box,
  type BoxProps,
  Flex,
  Icon,
  NavLink,
  type NavLinkProps,
  Tooltip,
} from "metabase/ui";

import { useOmniPickerContext } from "../../context";
import type { OmniPickerItem } from "../../types";
import { isSelectedItem, useGetEntityPickerIcon } from "../../utils";
import { ItemListLoader } from "../ItemListLoader";

const PickerColumn = ({
  children,
  ...props
}: {
  children: React.ReactNode;
}) => (
  <Box p="1rem" {...props}>
    {children}
  </Box>
);

interface ItemListProps {
  pathIndex: number;
  items?: OmniPickerItem[];
  isLoading?: boolean;
  error?: unknown;
  navLinkProps?: (isSelected?: boolean) => NavLinkProps;
  containerProps?: BoxProps;
}

export function ItemList({
  pathIndex,
  items,
  isLoading = false,
  error,
  navLinkProps,
  containerProps = { pb: "xxs" },
}: ItemListProps) {
  const tc = useTranslateContent();
  const {
    path,
    setPath,
    isHiddenItem,
    isDisabledItem,
    isFolderItem,
    isSelectableItem,
    onChange,
    options,
  } = useOmniPickerContext();
  const selectedItem = path?.[pathIndex + 1];
  const filteredItems = useMemo(() => {
    return items ? items.filter((i) => !isHiddenItem(i)) : items;
  }, [items, isHiddenItem]);
  const isCurrentLevel = path.length - 2 === pathIndex;
  const isTenantUser = useSelector(getIsTenantUser);
  const getEntityPickerIcon = useGetEntityPickerIcon();

  const activeItemIndex = useMemo(() => {
    if (!filteredItems || !selectedItem) {
      return -1;
    }

    return filteredItems.findIndex((item) =>
      isSelectedItem(item, selectedItem),
    );
  }, [filteredItems, selectedItem]);

  if (error) {
    return <LoadingAndErrorWrapper error={error} />;
  }

  if (isLoading) {
    return <ItemListLoader />;
  }

  if (!filteredItems || !filteredItems.length) {
    return null;
  }

  return (
    <VirtualizedList
      Wrapper={PickerColumn}
      scrollTo={activeItemIndex}
      estimatedItemSize={37}
    >
      {filteredItems.map((item: OmniPickerItem, index) => {
        const isSelected = index === activeItemIndex;
        const icon = getEntityPickerIcon(item, {
          isSelected: isSelected && isCurrentLevel,
          isTenantUser,
        });
        const isDisabled = isDisabledItem(item);
        const tooltip = options.getItemTooltip?.(item);

        return (
          <Box
            data-testid="picker-item"
            key={`${item.model}-${item.id}`}
            {...containerProps}
          >
            <Tooltip label={tooltip} disabled={!tooltip} position="right">
              <NavLink
                w={"auto"}
                disabled={isDisabled}
                style={
                  isDisabled && tooltip ? { pointerEvents: "all" } : undefined
                }
                rightSection={
                  isFolderItem(item) ? (
                    <Icon name="chevronright" size={10} />
                  ) : null
                }
                mb={0}
                label={
                  <Flex align="center">
                    {tc(item.name)}{" "}
                    <PLUGIN_MODERATION.ModerationStatusIcon
                      status={
                        "moderated_status" in item && item.moderated_status
                      }
                      filled
                      size={14}
                      ml="0.5rem"
                    />
                  </Flex>
                }
                active={isSelected}
                leftSection={<EntityIcon {...icon} />}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault(); // prevent form submission
                  e.stopPropagation(); // prevent parent onClick

                  if (isDisabled) {
                    return;
                  }

                  setPath((prevPath) => [
                    ...prevPath.slice(0, pathIndex + 1),
                    item,
                  ]);

                  if (!options?.hasConfirmButtons && isSelectableItem(item)) {
                    onChange(item);
                  }
                }}
                variant={isCurrentLevel ? "primary" : "secondary"}
                {...navLinkProps?.(isSelected)}
              />
            </Tooltip>
          </Box>
        );
      })}
    </VirtualizedList>
  );
}
